import { useCallback, useMemo, useState } from 'react'
import {
  AlertIcon,
  CheckIcon,
  DashButton,
  DataSurface,
  InfoIcon,
  Note,
  PasswordInput,
  PrivacyIcon,
} from '../../../shared/components'
import { useResource } from '../../../shared/hooks/useResource'
import {
  createOperator,
  deactivateOperator,
  getOperators,
  getPlatformOverview,
  getSchools,
  type OperatorSummary,
  type PlatformOverview,
  type SchoolSummary,
} from '../../../shared/api/platform'
import type { StaffAuth } from '../../../shared/api/teaching'
import type { Lang } from '../../../shared/i18n'
import { PLATFORM_COPY, describePlatformError, passwordProblemText } from './copy'
import {
  OPERATOR_PASSWORD_MAX,
  OPERATOR_PASSWORD_MIN,
  classifyWriteError,
  passwordState,
  type WriteFailure,
} from './platformModel'

/* ─────────────────────────────────────────────────── Role Data Types ── */

/*
 * ✅ `RoleDef`, `BASE_ROLES`, `PermissionRow` AND `PERMISSIONS` ARE GONE, ON
 * REQUEST — and they described a product that does not exist.
 *
 * 🔴 THIS PLATFORM'S ROLES ARE A DATABASE ENUM, NOT RECORDS. `Role` in
 * `user.prisma` is ADMIN, TEACHER, PARENT, SUPER_ADMIN, PRINCIPAL and
 * COORDINATOR, fixed at migration time; `ScopeService` switches on it with a
 * `default:` that REFUSES. There is no table of roles, nothing creates one, and
 * no route grants a permission — so a screen offering "Create New Role", a
 * sixteen-row permission matrix and an editable "Content Manager" was offering
 * an administration model the server has never had.
 *
 * ⚠️ THE MATRIX WAS ALSO WRONG WHERE IT OVERLAPPED WITH REALITY. It listed
 * Assignments, Courses / Curriculum, Backup & Restore and System Support as
 * modules — none of which this product has — and gave "Student" a role row,
 * though a child authenticates as a DEVICE and never as a user. An operator
 * reading it would have believed they had checked an access rule they had in
 * fact invented.
 *
 * What is left on this screen is what the server can actually answer: who the
 * operators are, how many accounts each role holds, and the form that creates
 * another operator.
 */

/* ─────────────────────────────────────────────────────────── Component ── */

export interface PlatformOperatorsScreenProps {
  lang?: Lang
  auth?: StaffAuth
}

export function PlatformOperatorsScreen({
  lang = 'en',
  auth,
}: PlatformOperatorsScreenProps) {
  const t = PLATFORM_COPY[lang]
  const [showAddForm, setShowAddForm] = useState<boolean>(false)

  // Real backend resources
  const operatorsResource = useResource<OperatorSummary[]>(
    (signal) => getOperators({ ...auth, signal }),
    ['platform-operators'],
    { isEmpty: (rows) => rows.length === 0 },
  )

  const schoolsResource = useResource<SchoolSummary[]>(
    (signal) => getSchools({ ...auth, signal }),
    ['platform-schools'],
    { isEmpty: (rows) => rows.length === 0 },
  )

  const overviewResource = useResource<PlatformOverview>(
    (signal) => getPlatformOverview({ ...auth, signal }),
    ['platform-overview'],
  )
  const overview = overviewResource.data

  const operators = useMemo(() => operatorsResource.data ?? [], [operatorsResource.data])
  const schools = useMemo(() => schoolsResource.data ?? [], [schoolsResource.data])

  // Calculate real metrics from active platform data
  const realSuperAdminsCount = operators.length
  const realSchoolAdminsCount = schools.length
  /*
   * 🔴 THESE COME FROM F1's OVERVIEW, NOT FROM `getSchools`. `SchoolSummary`
   * carries no seat or teacher count — it never has — so the sum was over two
   * fields that do not exist and every tile read zero. `PlatformOverview` is
   * the one place the platform counts itself, folded server-side in a single
   * query (`platform-metrics.ts`), which is also why this screen must not loop
   * over schools to build the same numbers.
   */
  const realStudentsCount = overview?.enrolment.seats ?? 0
  const realTeachersCount = overview?.teachers.active ?? 0
  /*
   * 🔴 NULL, NOT AN ESTIMATE. This read `students × 0.95` — a fabricated figure
   * on an operator screen, indistinguishable from a counted one. Nothing in the
   * product counts parents yet, so the tile says so; a dash is a smaller lie
   * than a number nobody measured.
   */
  const realParentsCount: number | null = null
  /* ⚠️ There is no content-manager role in the schema (`Role`: SUPER_ADMIN,
     ADMIN, PRINCIPAL, TEACHER, COORDINATOR, PARENT). The tile stays because the
     permissions matrix lists the role; the count is unknown, not three. */
  const realContentManagersCount: number | null = null

  /*
   * 🔴 `null` MEANS "NOT COUNTED", AND IS RENDERED AS A DASH. It is not zero:
   * zero is a fact ("no teachers yet") and this product has tiles for both.
   */
  const roleCounts: Record<string, number | null> = useMemo(
    () => ({
      'super-admin': realSuperAdminsCount,
      'school-admin': realSchoolAdminsCount,
      teacher: realTeachersCount,
      parent: realParentsCount,
      student: realStudentsCount,
      'content-manager': realContentManagersCount,
    }),
    [
      realSuperAdminsCount,
      realSchoolAdminsCount,
      realTeachersCount,
      realParentsCount,
      realStudentsCount,
    ],
  )

  /** Only what was actually counted. An uncounted role adds nothing. */
  const totalRoleAssignments = useMemo(
    () => Object.values(roleCounts).reduce<number>((a, b) => a + (b ?? 0), 0),
    [roleCounts],
  )

  return (
    <section className="font-dash text-dash-sm text-ink pb-12 space-y-6">
      {/* 1. Header & Top Action Buttons */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-dash text-dash-2xl font-extrabold text-ink">Operators</h1>
          {/* ⚠️ IT NO LONGER PROMISES PERMISSION MANAGEMENT. Roles are a fixed
              enum in the database; nothing on this screen can change what a role
              may do, and the old subtitle said it could. */}
          <p className="mt-1 font-dash text-dash-sm text-muted">
            The accounts that can open every school on Kalqy.
          </p>
        </div>

        <div className="flex items-center gap-3 shrink-0">
          {/* ✅ "Export Roles" IS GONE. It had no `onClick`, and there is no
              role table to export — the roles are a database enum. */}
          <button
            type="button"
            onClick={() => setShowAddForm(!showAddForm)}
            className="flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-dash-xs font-bold text-white shadow-card hover:bg-primary-hover transition-colors cursor-pointer"
          >
            <span className="text-sm font-extrabold">+</span>
            <span>Add an operator</span>
          </button>
        </div>
      </div>

      {/*
        ✅ FOUR OF THESE SIX SAID "Using Real Backend Data" AND WERE LITERALS.
        "Total Roles: 6", "Active Roles: 6", "Total Permissions: 78" and "Custom
        Roles: 0" were typed into the JSX — 78 being the cell count of a matrix
        that no longer exists, and "Custom Roles" counting a thing this product
        cannot create. "Last Updated: Live DB / Synced" was a decoration.

        What is left is the one figure that was ever computed: how many accounts
        exist per role, summed. It keeps its denominator on the card, because two
        of the roles it sums cannot be counted at all (see `roleCounts`).
      */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="rounded-2xl border border-line bg-surface p-4 shadow-card">
          <span className="block text-dash-xs font-bold text-muted">Platform operators</span>
          <span className="my-1 block text-dash-xl font-extrabold tabular text-ink">
            {operators.length.toLocaleString()}
          </span>
          <span className="block text-dash-xs font-medium text-muted">
            Accounts that can open every school
          </span>
        </div>

        <div className="rounded-2xl border border-line bg-surface p-4 shadow-card">
          <span className="block text-dash-xs font-bold text-muted">Schools on the platform</span>
          <span className="my-1 block text-dash-xl font-extrabold tabular text-ink">
            {schools.length.toLocaleString()}
          </span>
          <span className="block text-dash-xs font-medium text-muted">Every status</span>
        </div>

        <div className="rounded-2xl border border-line bg-surface p-4 shadow-card">
          <span className="block text-dash-xs font-bold text-muted">Role assignments</span>
          <span className="my-1 block text-dash-xl font-extrabold tabular text-ink">
            {totalRoleAssignments.toLocaleString()}
          </span>
          {/* ⚠️ NAMED, BECAUSE THE SUM IS NOT EVERYBODY. `roleCounts` leaves
              parents and content managers null — there is no count for them —
              and a total that quietly omitted two roles would read as complete. */}
          <span className="block text-dash-xs font-medium text-muted">
            Operators, school admins, teachers and children
          </span>
        </div>
      </div>

      {/* 3. Add Operator / Create Role Collapsible Form */}
      {showAddForm && (
        <div className="pt-2">
          <AddOperatorForm lang={lang} auth={auth} onCreated={operatorsResource.retry} />
        </div>
      )}

      {/*
        ✅ THE TWO-COLUMN ROLE BROWSER IS GONE, ON REQUEST. A list of six
        invented roles on the left selected into a "Role Details" panel on the
        right: a permission matrix of sixteen modules by six roles, an "Edit
        Role" button that saved nothing, and a "Role History" tab whose one entry
        read "Role permissions synced from platform security policy
        configuration" — a sentence about a system this product does not run.
        Every cell in that matrix was a literal.

        ⚠️ THE REAL OPERATOR LIST LIVED INSIDE IT, on the second tab, and is
        promoted here rather than deleted with the panel. It is the only thing on
        this screen the server answers: `GET /superadmin/operators`.
      */}
      <div className="rounded-2xl border border-line bg-surface p-4 sm:p-6 shadow-card">
        <div className="mb-4 flex items-center justify-between border-b border-line pb-4">
          <h2 className="text-dash-md font-extrabold text-ink">Platform operators</h2>
          <span className="text-dash-xs font-bold text-muted tabular">
            {operators.length} {operators.length === 1 ? 'account' : 'accounts'}
          </span>
        </div>

        <DataSurface
          state={operatorsResource.state}
          skeletonRows={3}
          emptyMessage={t.operatorsOnlyOne}
          errorMessage={describePlatformError(operatorsResource.error, lang)}
          errorRetryLabel={t.retry}
          onRetry={operatorsResource.retry}
        >
          <OperatorTable
            rows={operators}
            lang={lang}
            auth={auth}
            onChanged={operatorsResource.retry}
          />
        </DataSurface>
      </div>

    </section>
  )
}

function OperatorTable({
  rows,
  lang,
  auth,
  onChanged,
}: {
  rows: readonly OperatorSummary[]
  lang: Lang
  auth?: StaffAuth
  onChanged: () => void
}) {
  const t = PLATFORM_COPY[lang]
  const [busy, setBusy] = useState(false)
  const [confirming, setConfirming] = useState<string | null>(null)
  const [ended, setEnded] = useState<number | null>(null)
  const [failure, setFailure] = useState<WriteFailure | null>(null)

  const deactivate = useCallback(
    async (userId: string) => {
      setBusy(true)
      setFailure(null)
      setConfirming(null)
      try {
        const result = await deactivateOperator(userId, auth)
        setEnded(result.revokedSessions)
        onChanged()
      } catch (err) {
        setFailure(classifyWriteError(err))
      } finally {
        setBusy(false)
      }
    },
    [auth, onChanged],
  )

  return (
    <>
      <div className="rounded-2xl border border-line bg-surface shadow-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-line bg-gray-50/70 text-dash-xs font-bold text-muted">
                <th className="px-5 py-3.5">{t.operatorName}</th>
                <th className="px-5 py-3.5">{t.operatorEmail}</th>
                <th className="px-5 py-3.5">{t.operatorState}</th>
                <th className="px-5 py-3.5">{t.operatorAdded}</th>
                <th className="px-5 py-3.5 text-right"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line/60">
              {rows.map((row) => (
                <tr
                  key={row.userId}
                  className={`hover:bg-gray-50/40 transition-colors ${row.userId === confirming ? 'bg-rose-50/30' : ''}`}
                >
                  <td className="px-5 py-4 font-bold text-ink text-dash-sm">{row.name ?? t.dash}</td>
                  <td className="px-5 py-4 text-dash-xs text-muted">{row.email ?? t.dash}</td>
                  <td className="px-5 py-4">
                    {row.awaitingClaim ? (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1 text-dash-xs font-bold text-amber-700 border border-amber-200/60">
                        <InfoIcon size={13} />
                        {t.operatorAwaiting}
                      </span>
                    ) : row.isActive ? (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-dash-xs font-bold text-emerald-700 border border-emerald-200/60">
                        <CheckIcon size={13} />
                        {t.operatorActive}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1 text-dash-xs font-medium text-gray-600">
                        <InfoIcon size={13} />
                        {t.operatorInactive}
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-4 text-dash-xs text-muted">
                    {new Date(row.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-5 py-4 text-right">
                    {row.mayDeactivate ? (
                      <DashButton
                        variant="destructive"
                        disabled={busy}
                        onClick={() => setConfirming(row.userId)}
                      >
                        {busy ? t.operatorDeactivating : t.operatorDeactivate}
                      </DashButton>
                    ) : (
                      <span className="text-dash-xs font-bold text-muted">System Admin</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="border-t border-line bg-gray-50/60 p-3.5 px-5 flex items-center gap-2.5 text-dash-xs text-muted">
          <InfoIcon size={16} className="text-muted shrink-0" />
          <span>
            {t.operatorCannotSelf} {t.operatorCannotLast}
          </span>
        </div>
      </div>

      {confirming !== null && (
        <div
          className="mt-4 max-w-prose rounded-2xl border border-rose-200 bg-rose-50/60 p-4 shadow-sm"
          role="alertdialog"
          aria-label={t.operatorConfirm}
        >
          <p className="font-bold text-rose-900">{t.operatorConfirm}</p>
          <p className="mt-1 text-dash-xs text-rose-700">{t.operatorConfirmNote}</p>
          <div className="mt-3 flex gap-2">
            <DashButton
              variant="destructive"
              disabled={busy}
              onClick={() => void deactivate(confirming)}
            >
              {t.confirm}
            </DashButton>
            <DashButton variant="ghost" onClick={() => setConfirming(null)}>
              {t.cancel}
            </DashButton>
          </div>
        </div>
      )}

      {ended !== null && (
        <div className="mt-4 max-w-prose">
          <Note tone="success" icon={<CheckIcon size={16} />}>
            {t.operatorDeactivated(ended)}
          </Note>
        </div>
      )}

      {failure !== null && (
        <div className="mt-4 max-w-prose">
          <Note tone="warning" icon={<AlertIcon size={16} />}>
            {failure === 'REFUSED' ? t.notAnOperator : t.operatorDeactivateFailed}
          </Note>
        </div>
      )}
    </>
  )
}

function AddOperatorForm({
  lang,
  auth,
  onCreated,
}: {
  lang: Lang
  auth?: StaffAuth
  onCreated: () => void
}) {
  const t = PLATFORM_COPY[lang]
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<WriteFailure | null>(null)
  const [created, setCreated] = useState<string | null>(null)

  const pw = useMemo(
    () => passwordState(password, confirm, { email: email.trim() }),
    [password, confirm, email],
  )

  const ready = name.trim().length > 0 && email.trim().length > 0 && pw.kind === 'ready'

  const add = useCallback(async () => {
    if (pw.kind !== 'ready') return
    setBusy(true)
    setFailure(null)
    setCreated(null)
    try {
      const row = await createOperator(
        { email: email.trim(), name: name.trim(), password: pw.password },
        auth,
      )
      setCreated(row.name)
      setName('')
      setEmail('')
      setPassword('')
      setConfirm('')
      onCreated()
    } catch (err) {
      setFailure(classifyWriteError(err))
    } finally {
      setBusy(false)
    }
  }, [email, name, pw, auth, onCreated])

  return (
    <div className="rounded-2xl border border-line bg-surface p-4 sm:p-6 shadow-card">
      <form
        onSubmit={(e) => {
          e.preventDefault()
          void add()
        }}
      >
        <div className="flex items-center gap-2.5 mb-6">
          <div className="h-9 w-9 rounded-xl bg-pink-50 text-pink-600 flex items-center justify-center shrink-0">
            <PrivacyIcon size={18} />
          </div>
          {/* ⚠️ IT CREATES AN OPERATOR AND ONLY AN OPERATOR. The form posts to
              `POST /superadmin/operators`; there was never a role half to it,
              and the heading said there was. */}
          <h2 className="text-dash-lg font-extrabold text-ink">Add an operator</h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mb-6">
          <div>
            <label htmlFor="operator-name" className="block text-dash-xs font-bold text-muted mb-1.5">
              Full Name
            </label>
            <input
              id="operator-name"
              type="text"
              placeholder="Enter full name"
              value={name}
              maxLength={120}
              disabled={busy}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-xl border border-line bg-white px-3.5 py-2.5 text-dash-xs text-ink placeholder:text-muted/60 focus:border-primary focus:outline-none transition-colors"
            />
          </div>
          <div>
            <label htmlFor="operator-email" className="block text-dash-xs font-bold text-muted mb-1.5">
              Email Address
            </label>
            <input
              id="operator-email"
              type="email"
              autoComplete="off"
              placeholder="Enter email address"
              value={email}
              maxLength={200}
              disabled={busy}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-xl border border-line bg-white px-3.5 py-2.5 text-dash-xs text-ink placeholder:text-muted/60 focus:border-primary focus:outline-none transition-colors"
            />
          </div>
        </div>

        <h3 className="text-dash-sm font-extrabold text-ink mb-1">Password Credentials</h3>
        {/* 🔴 THIS SENTENCE SAID "AT LEAST 8" AGAINST A MINIMUM OF 12, AND THAT
            IS WHY THE FORM READ AS BROKEN. An operator typed the nine characters
            the screen asked for, "Create Account" stayed grey, and there was
            nothing anywhere saying which rule had refused it — see the note
            under the boxes, which `pw` was computed for and never rendered
            into. The number now comes from the constant the check uses. */}
        <p className="text-dash-xs text-muted mb-3 max-w-prose">
          {t.operatorPasswordHint(OPERATOR_PASSWORD_MIN)}
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mb-4">
          <div>
            <label htmlFor="operator-password" className="block text-dash-xs font-bold text-muted mb-1.5">
              Password
            </label>
            {/* 🔴 A REVEAL, BECAUSE THIS PASSWORD IS READ OUT LOUD. The operator
                types a credential they then have to hand to a school admin over
                the phone; a field they cannot see is a field they mistype and
                cannot check. It is `PasswordInput`'s eye now rather than two
                text links under the boxes — the same control every other
                password field in the product has. */}
            <PasswordInput
              id="operator-password"
              autoComplete="new-password"
              placeholder="Enter password"
              value={password}
              maxLength={OPERATOR_PASSWORD_MAX}
              disabled={busy}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-xl border border-line bg-white px-3.5 py-2.5 text-dash-xs text-ink placeholder:text-muted/60 focus:border-primary focus:outline-none transition-colors"
            />
          </div>
          <div>
            <label htmlFor="operator-password-confirm" className="block text-dash-xs font-bold text-muted mb-1.5">
              Confirm Password
            </label>
            <PasswordInput
              id="operator-password-confirm"
              autoComplete="new-password"
              placeholder="Re-enter password"
              value={confirm}
              maxLength={OPERATOR_PASSWORD_MAX}
              disabled={busy}
              onChange={(e) => setConfirm(e.target.value)}
              className="w-full rounded-xl border border-line bg-white px-3.5 py-2.5 text-dash-xs text-ink placeholder:text-muted/60 focus:border-primary focus:outline-none transition-colors"
            />
          </div>
        </div>

        {/* 🔴 `pw` DECIDED WHETHER THE BUTTON WORKED AND WAS NEVER DRAWN. It is
            computed above, it gates `ready`, and every one of its refusals —
            too short, one character class, predictable, echoes the email, the
            two boxes disagree — reached the operator as a button that did
            nothing. `CreateSchoolForm` has rendered exactly these three notes
            since F1; this form was the copy that lost them. */}
        <div className="mb-4 max-w-prose">
          {pw.kind === 'empty' && (
            <Note tone="info" icon={<InfoIcon size={16} />}>
              {t.passwordRequiredNote}
            </Note>
          )}
          {pw.kind === 'ready' && (
            <Note tone="success" icon={<CheckIcon size={16} />}>
              {t.operatorPasswordReadyNote}
            </Note>
          )}
          {pw.kind === 'problem' && (
            <Note tone="warning" icon={<AlertIcon size={16} />}>
              {passwordProblemText(pw.problem, lang)}
            </Note>
          )}
        </div>

        <button
          type="submit"
          disabled={busy || !ready}
          className="bg-primary text-white px-5 py-2.5 rounded-full font-bold text-dash-xs hover:bg-primary-hover transition-colors flex items-center gap-2 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
        >
          <span>{busy ? 'Creating...' : 'Create Account'}</span>
        </button>

        {/* 🔴 `failure` WAS CLASSIFIED AND NEVER SHOWN. A taken email and a
            forbidden session both left the form looking untouched, which reads
            as "the button is broken" and produces the retry that takes the
            email a second time. */}
        {failure !== null && (
          <div className="mt-4 max-w-prose">
            <Note tone="danger" icon={<AlertIcon size={16} />}>
              {failure === 'EMAIL_TAKEN' || failure === 'CONFLICT'
                ? t.emailTaken
                : failure === 'REFUSED'
                  ? t.notAnOperator
                  : t.loadFailed}
            </Note>
          </div>
        )}

        {created !== null && (
          <div className="mt-4 max-w-prose">
            <Note tone="success" icon={<CheckIcon size={16} />}>
              Operator account created successfully for {created}.
            </Note>
          </div>
        )}
      </form>
    </div>
  )
}
